import React from 'react';
import { PMNode, NODE_CATALOG_BY_SUBTYPE, resolveNodeSubtype } from '../../types';
import { hexPoints } from '../../utils/geometry';
import type { SemanticNodeOperator } from '../../utils/mapping/semanticStyles';

interface NodeProps {
  n:           PMNode;
  isSelected:  boolean;
  isInFlow:    boolean;
  flowColor?:  string;
  onMouseDown: (e: React.MouseEvent) => void;
  onClick:     (e: React.MouseEvent) => void;
  onDblClick:  (e: React.MouseEvent) => void;
  semanticOperator?: SemanticNodeOperator;
  isPlaybackVisible?: boolean;
}

const Node: React.FC<NodeProps> = ({
  n, isSelected, isInFlow, flowColor,
  onMouseDown, onClick, onDblClick, semanticOperator = 'normal', isPlaybackVisible = true,
}) => {
  const { x, y, label, initials } = n;
  const resolvedSubtype = resolveNodeSubtype(n.type, n.subtype);
  const meta = semanticOperator === 'if'
    ? NODE_CATALOG_BY_SUBTYPE.event
    : NODE_CATALOG_BY_SUBTYPE[resolvedSubtype];

  const semanticStroke = semanticOperator === 'exception' ? '#c0392b' : undefined;
  const stroke = isSelected ? '#4a9e6b' : semanticStroke ?? (isInFlow && flowColor ? flowColor : '#bbb');
  const strokeW = isInFlow ? 2.5 : 1.5;
  const shapeFill = isSelected
    ? meta.style.palette?.selectedBg ?? '#4a9e6b'
    : semanticOperator === 'wait' ? '#fff8e8' : '#fff';
  const labelFill = isSelected ? (meta.style.palette?.selectedText ?? 'white') : '#555';

  const shapeProps = {
    fill: shapeFill,
    stroke,
    strokeWidth: strokeW,
    style: { transition: 'stroke 0.15s, fill 0.15s' } as React.CSSProperties,
  };
  console.log(meta)

  if (!meta) return null;
  const labelOffY = y <= 0 ? -(meta.style?.labelOffset ?? 18) : (meta.style?.labelOffset ?? 18);

  const renderShape = () => {
    switch (meta.style.shape) {
      case 'hex':
        return <polygon points={hexPoints(x, y, meta.style.radius ?? 12)} {...shapeProps} className="node-hex" />;
      case 'rect': {
        const width = meta.style.width ?? 28;
        const height = meta.style.height ?? 18;
        return <rect x={x - width / 2} y={y - height / 2} width={width} height={height} rx={meta.style.rx ?? 5} {...shapeProps} className="node-soft" />;
      }
      case 'bubble': {
        const width = meta.style.width ?? 26;
        const height = meta.style.height ?? 18;
        return (
          <>
            <rect x={x - width / 2} y={y - height / 2} width={width} height={height} rx={meta.style.rx ?? 5} {...shapeProps} className="node-bubble" />
            <polygon points={`${x - 4},${y + height / 2} ${x + 4},${y + height / 2} ${x - 2},${y + height / 2 + 6}`} {...shapeProps} className="node-bubble" />
          </>
        );
      }
      case 'diamond': {
        const width = meta.style.width ?? 72;
        const height = meta.style.height ?? 44;
        return <rect x={x - width / 2} y={y - height / 2} width={width} height={height} rx={meta.style.rx ?? 10} {...shapeProps} className="node-diamond" />;
      }
      case 'circle':
      default:
        return <circle cx={x} cy={y} r={meta.style.radius ?? 10} {...shapeProps} className="node-circle" />;
    }
  };

  const canShowDeleteBadge = meta.deletable !== false;

  return (
    <g
      className={`node-g${isSelected ? ' selected' : ''}${isInFlow ? ' in-flow' : ''}`}
      data-id={n.id}
      style={{ cursor: 'grab', opacity: isPlaybackVisible ? 1 : 0.22, transition: 'opacity 0.25s ease' }}
      onMouseDown={onMouseDown}
      onClick={onClick}
      onDoubleClick={onDblClick}
    >
      {renderShape()}

      {meta.icon && (
        <text x={x} y={y} textAnchor="middle" dominantBaseline="central" fontSize={9} pointerEvents="none">
          {meta.icon}
        </text>
      )}

      {resolvedSubtype === 'person' && initials && (
        <text
          x={x}
          y={y}
          textAnchor="middle"
          dominantBaseline="central"
          fontFamily="'Caveat',cursive"
          fontSize={9}
          fontWeight={700}
          fill={isSelected ? '#ffffff' : '#2d7a50'}
          pointerEvents="none"
        >
          {initials}
        </text>
      )}

      <text
        x={x}
        y={y + labelOffY}
        textAnchor="middle"
        fontFamily="'Caveat',cursive"
        fontSize={12}
        fill={labelFill}
        pointerEvents="none"
        style={{ userSelect: 'none' }}
        className="node-label"
      >
        {label}
      </text>

      {canShowDeleteBadge && (
        <text
          x={x}
          y={y}
          textAnchor="middle"
          dominantBaseline="central"
          fontSize={9}
          fill="#c0392b"
          pointerEvents="none"
          className="node-del"
        >
          ✕
        </text>
      )}

      {semanticOperator === 'parallel' && (
        <text x={x} y={y - 1} textAnchor="middle" dominantBaseline="central" fontSize={10} fill={stroke} pointerEvents="none">∥</text>
      )}
      {semanticOperator === 'wait' && (
        <text x={x} y={y - 1} textAnchor="middle" dominantBaseline="central" fontSize={9} fill={stroke} pointerEvents="none">⏸</text>
      )}
    </g>
  );
};

export default Node;
